import { styled } from '@abyss/web/tools/styled';
import { Button } from '@abyss/web/ui/Button';

const FONT_FAMILY = '"UHC Sans", Helvetica, Arial, sans-serif';
const FONT_FAMILY_SERIF = '"UHC Serif", Georgia, serif';

export const SLIDE_KEYFRAMES = `
  @keyframes pdSlideInRight {
    from { opacity: 0; transform: translateX(48px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes pdSlideInLeft {
    from { opacity: 0; transform: translateX(-48px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .pd-slide-right { animation: pdSlideInRight 0.28s ease-out both; }
  .pd-slide-left  { animation: pdSlideInLeft  0.28s ease-out both; }

  /* Location search box — match TextInput style */
  .pd-location-wrapper .abyss-search-input-root {
    width: 100%;
    min-width: auto;
  }
  .pd-location-wrapper .abyss-search-input-input-container {
    height: 40px;
    border-radius: 8px !important;
    border: 1px solid #E6E6E6 !important;
    background: #FFFFFF;
    box-shadow: none !important;
  }
  .pd-location-wrapper .abyss-search-input-input {
    font-size: 14px !important;
    font-family: "UHC Sans", Helvetica, Arial, sans-serif !important;
    color: #333333 !important;
    font-weight: 400 !important;
    box-shadow: none !important;
    border: none !important;
    height: 38px;
  }
  .pd-location-wrapper .abyss-search-input-input-container:focus-within {
    border-color: #006DFF !important;
    box-shadow: 0 0 0 1px #006DFF !important;
  }
  .pd-location-wrapper .abyss-search-input-section {
    font-size: 14px;
    font-family: "UHC Sans", Helvetica, Arial, sans-serif;
  }
`;

export const PDF_GENERATING_KEYFRAMES = `
  @keyframes pdfSpin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const PRINT_STYLES = `
  @page {
    margin: 0.55in;
  }

  html {
    background: #ffffff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  body {
    font-family: ${FONT_FAMILY};
    margin: 0;
    color: #202124;
    line-height: 1.28;
    font-size: 11.5px;
    background: #ffffff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  * {
    box-sizing: border-box;
  }

  .cover-page {
    page-break-after: always;
  }

  .cover-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 2px solid #3b3f45;
  }

  .cover-logo {
    width: 132px;
    height: auto;
    display: block;
  }

  .cover-date {
    font-size: 12px;
    font-weight: 700;
    color: #202124;
    letter-spacing: 0.03em;
  }

  .cover-title {
    font-family: ${FONT_FAMILY_SERIF};
    font-size: 32px;
    line-height: 1.1;
    color: #202124;
    margin: 0 0 2px;
  }

  .cover-summary-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 26px;
  }

  .cover-summary {
    font-size: 14px;
    line-height: 1.35;
    color: #202124;
  }

  .cover-plan {
    flex-shrink: 0;
    font-size: 14px;
    line-height: 1.35;
    color: #202124;
    text-align: right;
  }

  .cover-copy {
    padding-top: 0;
    font-size: 14px;
    line-height: 1.35;
  }

  .cover-copy p {
    margin: 0 0 14px;
  }

  .cover-list-title {
    margin: 0 0 8px;
    font-weight: 400;
  }

  .cover-list {
    margin: 0 0 14px 28px;
    padding: 0;
  }

  .cover-list li {
    margin: 0 0 4px;
    padding-left: 6px;
  }

  .cover-strong {
    font-weight: 700;
  }

  .cover-footer-rule {
    margin: 36px 0 18px;
    border-top: 2px solid #62666d;
  }

  .directory-section {
    break-inside: auto;
    page-break-inside: auto;
    margin-bottom: 24px;
    background: #ffffff;
  }

  .directory-referral-note {
    margin: 10px 0 0;
    page-break-inside: avoid;
  }

  .directory-referral-note__rule {
    border-top: 2px solid #2a2a2a;
    margin-bottom: 16px;
  }

  .directory-referral-note p {
    margin: 0;
    font-size: 11.5px;
    line-height: 1.35;
  }

  .directory-footer-page {
    break-inside: auto;
    page-break-inside: auto;
    padding-top: 18px;
    font-size: 11.5px;
    line-height: 1.35;
  }

  .directory-footer-page--with-divider {
    border-top: 2px solid #2a2a2a;
    padding-top: 18px;
  }

  .directory-footer-page p {
    margin: 0 0 22px;
  }

  .section-location {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.03em;
    margin: 0 0 18px;
    padding: 16px 38px;
    background: #000000;
    color: #ffffff;
    border: 2px solid #111111;
    text-transform: uppercase;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .section-specialty {
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 0.02em;
    margin: 0 0 16px;
    padding: 16px 38px;
    border: 2px solid #2a2a2a;
    background: #ffffff;
    text-transform: uppercase;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .provider-card {
    margin: 0 0 18px;
    background: #ffffff;
    page-break-inside: avoid;
  }

  .provider-top-divider {
    border-top: 2px solid #2a2a2a;
    margin-bottom: 16px;
  }

  .provider-header {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 3px;
  }

  .provider-header-meta {
    font-size: 11.5px;
    font-weight: 400;
    white-space: nowrap;
  }

  .provider-name {
    font-weight: 700;
    font-size: 14.5px;
    color: #202124;
  }

  .provider-subtitle {
    font-size: 11.5px;
    font-weight: 700;
    margin-bottom: 10px;
  }

  .provider-subtitle-meta {
    margin-bottom: 6px;
  }

  .provider-subtitle-meta:last-of-type {
    margin-bottom: 18px;
  }

  .provider-columns {
    display: flex;
    align-items: flex-start;
    gap: 22px;
  }

  .provider-column {
    min-width: 0;
  }

  .provider-column-primary {
    flex: 1.15;
  }

  .provider-column-contact {
    flex: 0.9;
  }

  .provider-column-status {
    flex: 0.82;
  }

  .provider-column-details {
    flex: 1.05;
  }

  .provider-address,
  .provider-distance,
  .provider-strong-line,
  .provider-id-line,
  .provider-phone-line,
  .provider-status-line,
  .provider-side-row {
    margin-bottom: 5px;
  }

  .provider-inline-label {
    font-weight: 700;
  }

  .provider-meta-label {
    font-weight: 400;
  }

  .provider-strong-line {
    font-weight: 400;
  }

  .provider-id-line {
    margin-top: 14px;
    font-weight: 700;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .provider-phone-line,
  .provider-status-line {
    white-space: pre-wrap;
  }

  .hours-block {
    margin-top: 18px;
    font-variant-numeric: tabular-nums;
  }

  .hours-row {
    display: grid;
    grid-template-columns: minmax(76px, max-content) minmax(0, 1fr);
    column-gap: 8px;
    align-items: start;
  }

  .hours-day {
    font-weight: 700;
    white-space: nowrap;
  }

  .hours-time {
    min-width: 0;
    white-space: nowrap;
  }

  .provider-side-row {
    white-space: pre-wrap;
  }

  .provider-side-label {
    display: inline;
    font-weight: 700;
    margin-right: 4px;
  }

  @media print {
    body {
      margin: 0;
    }
    .provider-card,
    .directory-referral-note {
      break-inside: avoid;
    }

    .section-location,
    .section-specialty {
      break-after: avoid;
    }
  }

  @media screen and (max-width: 800px) {
    .provider-columns {
      flex-direction: column;
      gap: 14px;
    }

    .hours-block {
      width: 100%;
    }
  }
`;

export const PdfGeneratingContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 20px',
  gap: '20px',
});

export const PdfSpinner = styled('div', {
  width: '50px',
  height: '50px',
  border: '4px solid #f3f3f3',
  borderTop: '4px solid #002677',
  borderRadius: '50%',
  animation: 'pdfSpin 1s linear infinite',
});

export const PdfGeneratingText = styled('div', {
  fontSize: '16px',
  fontWeight: '600',
  color: '#333333',
});

export const PageWrapper = styled('div', {
  minHeight: '100vh',
  width: '100%',
  backgroundColor: '#FFFFFF',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  boxSizing: 'border-box',
  fontFamily: FONT_FAMILY,
});

export const PageHeader = styled('div', {
  width: '100%',
  height: '88px',
  backgroundColor: '#FFFFFF',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '24px 32px',
  boxSizing: 'border-box',
  borderBottom: '1px solid #D6E2EC',
  flexShrink: 0,
  '@media (max-width: 600px)': {
    minHeight: '150px',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '24px 72px 28px 20px',
    gap: '12px',
    position: 'relative',
  },
});

export const HeaderTitle = styled('span', {
  fontFamily: FONT_FAMILY_SERIF,
  fontSize: '36px',
  fontWeight: 600,
  lineHeight: '40px',
  color: '#333333',
  '@media (max-width: 600px)': {
    fontSize: '28px',
    lineHeight: '34px',
    textAlign: 'center',
    maxWidth: '100%',
    wordBreak: 'break-word',
    paddingTop: '12px',
    paddingRight: '8px',
  },
});

export const HeaderMetaBlock = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: '2px',
  flex: 1,
  margin: '0 16px',
  '@media (max-width: 600px)': {
    flex: '0 1 auto',
    alignItems: 'center',
    margin: '0',
    gap: '10px',
    textAlign: 'center',
  },
});

export const HeaderMetaLabel = styled('span', {
  fontFamily: FONT_FAMILY,
  fontSize: '12px',
  fontWeight: 600,
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
  color: '#7C98B1',
  '@media (max-width: 600px)': {
    fontSize: '10px',
    lineHeight: '14px',
    color: '#757588',
  },
});

export const HeaderMetaValue = styled('span', {
  fontFamily: FONT_FAMILY,
  fontSize: '16px',
  fontWeight: 400,
  color: '#333333',
  '@media (max-width: 600px)': {
    fontSize: '18px',
    lineHeight: '26px',
  },
});

export const HeaderCloseButton = styled('button', {
  width: '40px',
  height: '40px',
  flexShrink: 0,
  border: '2px solid #002677',
  borderRadius: '50%',
  backgroundColor: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: '0',
  color: '#002677',
  '&:hover': {
    backgroundColor: 'rgba(0, 38, 119, 0.06)',
  },
  '@media (max-width: 600px)': {
    position: 'absolute',
    top: '24px',
    right: '20px',
    width: '52px',
    height: '52px',
  },
});

export const PageContentArea = styled('div', {
  width: '100%',
  paddingTop: '32px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  boxSizing: 'border-box',
});

export const ProgressBarContainer = styled('div', {
  display: 'flex',
  gap: '8px',
  width: '100%',
  maxWidth: '1048px',
});

export const ProgressSegment = styled('div', {
  flex: 1,
  height: '4px',
  borderRadius: '2px',
  transition: 'background-color 0.28s ease',
});

export const ContentBlock = styled('div', {
  width: '100%',
  maxWidth: '1048px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

export const StepTitle = styled('h2', {
  fontFamily: FONT_FAMILY,
  fontSize: '32.432px',
  fontWeight: 300,
  lineHeight: '40px',
  color: '#333333 !important',
  margin: '32px 0 16px 0',
  textAlign: 'center',
  width: '100%',
  maxWidth: '500px',
});

export const StepSubtitle = styled('p', {
  fontFamily: FONT_FAMILY,
  fontSize: '18px',
  fontWeight: 400,
  lineHeight: '32px',
  color: 'rgb(117, 117, 136)',
  margin: '0 0 40px 0',
  maxWidth: '1048px',
  textAlign: 'center',
  width: '100%',
});

export const StyledDivider = styled('div', {
  width: '100%',
  borderBottom: '1px solid #E6E6E6',
});

export const RadioOptionButton = styled('button', {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '16px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '20px 0',
  width: '100%',
  textAlign: 'left',
  fontFamily: FONT_FAMILY,
});

export const RadioCircle = styled('span', {
  flexShrink: 0,
  marginTop: '5px',
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  border: '2px solid #006DFF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
});

export const RadioDot = styled('span', {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: '#006DFF',
});

export const RadioTextWrapper = styled('span', {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

export const RadioTitle = styled('span', {
  fontFamily: FONT_FAMILY,
  fontSize: '24px',
  fontWeight: 400,
  color: '#006DFF',
  lineHeight: '30px',
});

export const RadioDescription = styled('span', {
  fontFamily: FONT_FAMILY,
  fontSize: '14px',
  fontWeight: 400,
  color: '#6B7280',
  lineHeight: '20px',
});

export const RadioGroupContainer = styled('div', {
  width: '100%',
});

export const LocationFormContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  marginTop: '8px',
});

export const StepContentWrapper = styled('div', {
  width: '100%',
});

/* Review Selections (Step 4) */
export const ReviewSummaryList = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
});

export const ReviewSummaryRow = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  padding: '24px',
  borderBottom: '1px solid #E6E6E6',
});

export const ReviewSummaryLabel = styled('span', {
  fontFamily: FONT_FAMILY,
  fontSize: '16px',
  fontWeight: 500,
  // letterSpacing: '0.4px',
  color: '#757588',
  marginBottom: '4px',
});

export const ReviewSummaryValue = styled('span', {
  fontFamily: FONT_FAMILY,
  fontSize: '32.43px',
  fontWeight: 300,
  color: '#333333',
  lineHeight: '32px',
});

export const ReviewWarningText = styled('p', {
  fontFamily: FONT_FAMILY,
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '20px',
  color: '#333333',
  textAlign: 'center',
  padding: '20px',
});

export const IconButtonInner = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
});

export const ButtonGroup = styled('div', {
  display: 'flex',
  gap: '16px',
  justifyContent: 'center',
  width: '100%',
  marginTop: '12px',
  '@media (max-width: 600px)': {
    flexDirection: 'column',
    gap: '10px',
    alignItems: 'center',
  },
});

export const SecondaryActionButton = styled(Button, {
  '&.abyss-button-root': {
    height: '40px',
    padding: '0 24px',
    borderRadius: '20px !important',
    border: '1px solid #8A8A8A !important',
    backgroundColor: '#FFFFFF !important',
    color: '#333333 !important',
    fontSize: '14px',
    fontFamily: FONT_FAMILY,
    '&:hover, &:focus': {
      backgroundColor: '#F2F2F2 !important',
      border: '1px solid #8A8A8A !important',
      color: '#333333 !important',
    },
    '&:active': {
      backgroundColor: '#EBEBEB !important',
      border: '1px solid #8A8A8A !important',
      color: '#333333 !important',
    },
    '@media (max-width: 600px)': {
      width: '88%',
      minWidth: '88%',
      height: '36px',
      padding: '0 14px',
      fontSize: '13px',
      borderRadius: '18px !important',
    },
  },
});

export const PrimaryActionButton = styled(Button, {
  '&.abyss-button-root': {
    minWidth: '170px',
    width: 'auto',
    height: '40px',
    padding: '0 24px',
    borderRadius: '20px !important',
    border: 'none !important',
    backgroundColor: '#002677 !important',
    color: '#FFFFFF !important',
    fontSize: '14px',
    fontFamily: FONT_FAMILY,
    transition: 'background-color 0.2s ease',
    '&:disabled': {
      backgroundColor: '#7A8FB5 !important',
      cursor: 'not-allowed',
      color: '#FFFFFF !important',
    },
    '&:hover:not(:disabled)': {
      backgroundColor: '#2d50cf !important',
    },
    '@media (max-width: 600px)': {
      width: '88%',
      minWidth: '88%',
      height: '36px',
      padding: '0 14px',
      fontSize: '13px',
      borderRadius: '18px !important',
      whiteSpace: 'normal',
      textAlign: 'center',
      lineHeight: '16px',
    },
  },
});
